import { useState, useEffect } from "react";
import usePersonalData from "../../../hooks/usePersonalData";
import useLinksData from "../../../hooks/useLinksData";
import useLeadData from "../../../hooks/useLeadData";
import useDealershipLogic from "../../../hooks/useDealershipLogic";
import useFormValidation from "../../../hooks/useFormValidation";
import useKintoOnePersonalData from "../../../hooks/useKintoOnePersonalData";
import { isRutValid } from "../../../../utils";

import FormHeader from "./components/FormHeader";
import PersonTypeSelector from "./components/PersonTypeSelector";
import PersonalDataForm from "./components/PersonalDataForm";
import DealershipSelector from "./components/DealershipSelector";
import PrivacyCheckbox from "./components/PrivacyCheckbox";

const ModalPersonalDataBodyNew = ({
    onCloseModal,
    onComplete,
    selectedModel,
    clientData = {},
    preselectedDealershipId = null,
}) => {
    const { getPersonalData, setPersonalData } = usePersonalData();
    const { getLinks } = useLinksData();
    const { createLead } = useLeadData();
    const { resetKintoForProductChange } = useKintoOnePersonalData();

    const dealershipLogic = useDealershipLogic(preselectedDealershipId);
    const formValidation = useFormValidation();

    const [activeBtn, setActiveBtn] = useState(clientData.company_name ? "company" : "person");
    const [name, setName] = useState({
        first_name: clientData.first_name || "",
        last_name: clientData.last_name || "",
    });
    const [localRut, setLocalRut] = useState(clientData.rut || "");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const personalData = getPersonalData();
    const links = getLinks();

    useEffect(() => {
        const initialData = {};

        if (clientData.first_name) initialData.first_name = clientData.first_name;
        if (clientData.last_name) initialData.last_name = clientData.last_name;
        if (clientData.first_name || clientData.last_name) {
            initialData.full_name = `${clientData.first_name || ""} ${clientData.last_name || ""}`.trim();
        }
        if (clientData.rut) initialData.rut = clientData.rut;
        if (clientData.phone_number) initialData.phone_number = clientData.phone_number;
        if (clientData.email) initialData.email = clientData.email;
        if (clientData.company_name) initialData.company_name = clientData.company_name;

        if (clientData.company_name) {
            initialData.type = "empresa";
        }

        if (Object.keys(initialData).length) {
            setPersonalData(initialData);
        }

        if (clientData.email) formValidation.validateEmail(clientData.email);
        if (clientData.phone_number) formValidation.validatePhoneNumber(clientData.phone_number);
        if (clientData.first_name) formValidation.validateName(clientData.first_name, "first_name");
        if (clientData.last_name) formValidation.validateName(clientData.last_name, "last_name");
        if (clientData.company_name) formValidation.validateName(clientData.company_name, "company_name");
    }, []);

    useEffect(() => {
        if (clientData.rut) return;

        const savedRut = personalData?.rut || "";
        if (savedRut && (savedRut.includes('.') || savedRut.includes('-'))) {
            setLocalRut(savedRut);
        }
    }, []);

    const isFormValid =
        name.first_name &&
        (activeBtn === "company" ? personalData.company_name : name.last_name) &&
        personalData.phone_number &&
        isRutValid(localRut) &&
        formValidation.checkboxValue &&
        !formValidation.invalidEmail &&
        !formValidation.invalidPhoneNumber &&
        !formValidation.invalidFirstName &&
        (activeBtn === "company" || !formValidation.invalidLastName) &&
        (activeBtn === "person" || !formValidation.invalidCompanyName) &&
        dealershipLogic.selectedDealership?.id;

    const handlePersonTypeChange = (personType) => {
        if (personType === activeBtn) return;

        setActiveBtn(personType);
        setPersonalData({
            type: personType === "company" ? "empresa" : "persona",
            ...(personType === "person" ? { company_name: "" } : {}),
        });
        // Cambia el producto Kinto (One Personal <-> One Business): descartar
        // la simulación y el lead del producto anterior
        resetKintoForProductChange();
    };

    useEffect(() => {
        if (name.first_name) {
            setPersonalData({
                full_name: `${name.first_name} ${name.last_name ? name.last_name : ""}`,
                first_name: name.first_name,
                last_name: name.last_name,
            });
        }
    }, [name]);

    const handleNameChange = (e) => {
        let trimmedValue = e.target.value;
        if (trimmedValue.length > 0 && trimmedValue[0] === " ") {
            trimmedValue = trimmedValue.substring(1);
        }
        
        const filteredValue = trimmedValue.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ\s\-]/g, '');
        
        setName({ ...name, [e.target.name]: filteredValue });
        
        if (filteredValue.trim().length > 0) {
            formValidation.validateName(filteredValue, e.target.name);
        } else {
            if (e.target.name === "first_name") {
                formValidation.validateName("", "first_name");
            } else if (e.target.name === "last_name") {
                formValidation.validateName("", "last_name");
            }
        }
    };

    const handlePersonalDataInputChange = (e) => {
        if (e.target.name === "email") formValidation.validateEmail(e.target.value);
        if (e.target.name === "phone_number") formValidation.validatePhoneNumber(e.target.value);
        
        if (e.target.name === "rut") {
            const newRutValue = e.target.value;
            setLocalRut(newRutValue);
            setPersonalData({ rut: newRutValue });
            return;
        }
        
        if (e.target.name === "company_name") {
            const filteredValue = e.target.value.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ\s\-]/g, '');
            
            setPersonalData({
                [e.target.name]: filteredValue,
            });
            
            if (filteredValue.trim().length > 0) {
                formValidation.validateName(filteredValue, "company_name");
            } else {
                formValidation.validateName("", "company_name");
            }
        } else {
            setPersonalData({
                [e.target.name]: e.target.value,
            });
        }
    };

    const handleRutBlur = () => {
        const normalizedRutValue = localRut.replace(/^0+(?=\d)/, "");

        if (normalizedRutValue !== localRut) {
            setLocalRut(normalizedRutValue);
            setPersonalData({ rut: normalizedRutValue });
        }
    };

    const finishLeadRegister = () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        createLead();
        if (onComplete) {
            onComplete();
        }
        onCloseModal();
    };

    return (
        <>
            <div className="modal-box new">
                <section>
                    <FormHeader selectedModel={selectedModel} />

                    <div className="group-grey --no-border-bottom">
                        <div className="group-grey__heading">
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" rx="16" fill="#E6E6E6"/>
                            <g clipPath="url(#clip0_4547_21838)">
                            <path d="M15.9974 16.0001C17.4707 16.0001 18.6641 14.8067 18.6641 13.3334C18.6641 11.8601 17.4707 10.6667 15.9974 10.6667C14.5241 10.6667 13.3307 11.8601 13.3307 13.3334C13.3307 14.8067 14.5241 16.0001 15.9974 16.0001ZM15.9974 17.3334C14.2174 17.3334 10.6641 18.2267 10.6641 20.0001V20.6667C10.6641 21.0334 10.9641 21.3334 11.3307 21.3334H20.6641C21.0307 21.3334 21.3307 21.0334 21.3307 20.6667V20.0001C21.3307 18.2267 17.7774 17.3334 15.9974 17.3334Z" fill="#292929"/>
                            </g>
                            <defs>
                            <clipPath id="clip0_4547_21838">
                            <rect width="16" height="16" fill="white" transform="translate(8 8)"/>
                            </clipPath>
                            </defs>
                            </svg>
                            <p className="nunito-16">Tus datos</p>
                        </div>
                        <PersonTypeSelector activeBtn={activeBtn} setActiveBtn={handlePersonTypeChange} />
                    </div>
                    <form className="modal-box__contact-form" id="personal-data-form">
                        <PersonalDataForm
                            activeBtn={activeBtn}
                            name={name}
                            personalData={personalData}
                            localRut={localRut}
                            invalidEmail={formValidation.invalidEmail}
                            invalidPhoneNumber={formValidation.invalidPhoneNumber}
                            invalidFirstName={formValidation.invalidFirstName}
                            invalidLastName={formValidation.invalidLastName}
                            invalidCompanyName={formValidation.invalidCompanyName}
                            handleNameChange={handleNameChange}
                            handlePersonalDataInputChange={handlePersonalDataInputChange}
                            handleRutBlur={handleRutBlur}
                        />

                        <DealershipSelector
                            dealerships={dealershipLogic.dealerships}
                            dealershipsLoading={dealershipLogic.dealershipsLoading}
                            userPosition={dealershipLogic.userPosition}
                            regions={dealershipLogic.regions}
                            brands={dealershipLogic.brands}
                            selectedRegion={dealershipLogic.selectedRegion}
                            selectedBrand={dealershipLogic.selectedBrand}
                            selectedDealership={dealershipLogic.selectedDealership}
                            preselectedDealershipId={preselectedDealershipId}
                            preselectedRegionValidated={dealershipLogic.preselectedRegionValidated}
                            preselectedBrandValidated={dealershipLogic.preselectedBrandValidated}
                            dealershipFilter={dealershipLogic.dealershipFilter}
                            handleRegionInputChange={dealershipLogic.handleRegionInputChange}
                            handleBrandInputChange={dealershipLogic.handleBrandInputChange}
                            handleDealershipInputChange={
                                dealershipLogic.handleDealershipInputChange
                            }
                        />

                        <PrivacyCheckbox
                            checkboxValue={formValidation.checkboxValue}
                            handleCheckboxChange={formValidation.handleCheckboxChange}
                            links={links}
                        />
                    </form>
                    <div className="modal-box__submit-btn">
                        <button
                            className="btn black"
                            onClick={() => finishLeadRegister()}
                            disabled={!isFormValid || isSubmitting}
                        >
                            Comenzar
                        </button>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ModalPersonalDataBodyNew;
